/**
 * wayai run-eval — run a scenario set's enabled evals and show results
 *
 * Creates a session, runs it, polls until completion, prints summary. A session
 * targets exactly one scenario set. With no flag the run endpoint resolves the
 * hub's sole enabled set; on multi-set hubs, pick one with `--set` / `--eval`.
 *
 * Usage:
 *   wayai run-eval                    # run the hub's enabled set and wait for results
 *   wayai run-eval --set <name>       # run the named scenario set
 *   wayai run-eval --eval <name>      # run ONLY that scenario (repeatable)
 *   wayai run-eval --eval a --eval b  # run just these two scenarios
 *   wayai run-eval --eval a --runs 2,6 # run only repetitions 2 and 6 of one scenario
 *   wayai run-eval --no-wait          # start session and exit (prints session ID)
 *   wayai run-eval --timeout 120      # wait max 2 minutes
 *   wayai run-eval --pacing conservative  # slower run pacing (safe for shared/tight providers)
 *   wayai run-eval --pacing 1500      # custom interval: 1500ms between runs
 *   wayai run-eval --fixture clinic-dense # override the suite fixture for this session
 *   wayai run-eval --no-queue         # fail fast instead of waiting for the fixture
 *   wayai run-eval --call-mode        # run every turn as a call turn is written (plan §12.1)
 *
 * `--call-mode` runs the session's turns as a voice call's are written: the pilot gets the
 * call-mode addendum and the reply gate takes no rewrite, and the hub's evaluator judges those
 * replies — the judged half of a call eval (`wayai eval call` scores the audio half).
 *
 * `--eval` SELECTS scenarios (gh #3748) — it no longer merely disambiguates
 * which set to run whole. Validating a change that touches three scenarios of a
 * 40-run set costs three scenarios of LLM spend, not forty. The selection is
 * written into the session at create time, so the session row records exactly
 * what it scored and can be reproduced verbatim. `--set` alone still runs the
 * whole set, which stays the default.
 *
 * Two conditions make this command WAIT by default rather than fail, both inside
 * the same `--timeout` budget: one Rekor preview base admits one eval session at
 * a time (gh #3745), and Rekor can briefly refuse the lease under its own write
 * backpressure (gh #3751). Both clear without anyone acting. `--no-queue`
 * restores the fail-fast behavior for scripts that would rather see the refusal,
 * and `--no-wait` implies it (that flag exists to free the terminal, so it must
 * not start blocking).
 */

import { requireAuth, getAccessToken } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient } from '../lib/api-client.js';
import { extractApiMessage } from '../lib/errors.js';
import { printResultsTable, printFailingRuns } from '../lib/eval-format.js';
import { EVAL_PACING_PRESET_NAMES, PACING_INTERVAL_MIN_MS, PACING_INTERVAL_MAX_MS, EVAL_MAX_RUNS_PER_SCENARIO, type PacingConfig } from '@wayai/core';
import { EVAL_FIXTURE_NAME_MAX_LENGTH, terminalRunCount, type EvalScenarioSelection } from '@wayai/core/contracts';
import { installEvalSignalHandlers, type EvalSignalController } from '../lib/eval-session-control.js';
import { runWithFixtureQueue, isQueueableLaunchError } from '../lib/eval-fixture-queue.js';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled', 'awaiting_review']);
const POLL_INTERVAL_MS = 3000;
const DEFAULT_TIMEOUT_S = 600;
// How many consecutive poll failures to ride out before giving up on the live
// view. At POLL_INTERVAL_MS this is ~15s of tolerance — enough to absorb a token
// rotation, KV propagation lag, or a network blip, while still failing fast on a
// genuinely broken connection rather than spinning silently until the deadline.
const MAX_CONSECUTIVE_POLL_ERRORS = 5;

export async function runEvalCommand(args: string[]): Promise<void> {
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  // Parse args
  let noWait = false;
  let noQueue = false;
  let timeoutSeconds = DEFAULT_TIMEOUT_S;
  let setName: string | undefined;
  const evalNames: string[] = [];
  let runNumbers: number[] | undefined;
  let pacing: PacingConfig | undefined;
  let fixtureOverride: string | undefined;
  let callMode = false;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--no-wait') {
      noWait = true;
    } else if (arg === '--no-queue') {
      noQueue = true;
    } else if (arg === '--call-mode') {
      callMode = true;
    } else if (arg === '--timeout') {
      timeoutSeconds = parseInt(args[++i], 10);
      if (isNaN(timeoutSeconds) || timeoutSeconds <= 0) {
        console.error('--timeout must be a positive number (seconds)');
        process.exit(1);
      }
    } else if (arg === '--set') {
      setName = args[++i];
      // The `--` guard matches `--eval`/`--fixture`: without it `--set --no-wait`
      // both misreports the set name and silently swallows the following flag.
      if (!setName || setName.startsWith('--')) {
        console.error('--set requires a scenario set name');
        process.exit(1);
      }
    } else if (arg === '--eval') {
      const name = args[++i];
      if (!name || name.startsWith('--')) {
        console.error('--eval requires an eval name');
        process.exit(1);
      }
      evalNames.push(name);
    } else if (arg === '--runs') {
      runNumbers = parseRunNumbers(args[++i]);
    } else if (arg === '--pacing') {
      pacing = parsePacing(args[++i]);
    } else if (arg === '--fixture') {
      const fixtureArg = args[++i];
      fixtureOverride = fixtureArg?.startsWith('--') ? undefined : fixtureArg?.trim();
      if (!fixtureOverride || fixtureOverride.length > EVAL_FIXTURE_NAME_MAX_LENGTH) {
        console.error(`--fixture requires a fixture name of at most ${EVAL_FIXTURE_NAME_MAX_LENGTH} characters`);
        process.exit(1);
      }
    }
  }

  // `--runs` names repetitions of ONE scenario. Spreading one run list across
  // several `--eval`s would be a guess about which scenario the numbers belong
  // to, and a wrong guess silently scores the wrong runs.
  if (runNumbers && evalNames.length !== 1) {
    console.error(
      evalNames.length === 0
        ? '--runs selects repetitions of a single scenario — name it with --eval <name>.'
        : '--runs applies to a single scenario, but several --eval flags were given. Run them one at a time.',
    );
    process.exit(1);
  }

  // The poll below can run for minutes — longer than a short-lived OAuth JWT.
  // Re-resolving the token on a 401 (refreshing if needed; way_ tokens pass
  // through unchanged) lets the client self-heal a mid-run expiry rather than
  // aborting the whole command (gh #2605).
  const client = new ApiClient({
    apiUrl: config.api_url,
    accessToken,
    onUnauthorized: () => getAccessToken(config),
  });

  // 1. Verify hub has enabled evals
  const evalsResult = await client.getEvals(hubId, { enabled: 'true' });
  const enabledCount = evalsResult.data.evals.length;

  if (enabledCount === 0) {
    console.error('No enabled evals found for this hub. Enable evals in the platform UI first.');
    process.exit(1);
  }

  console.log(`Found ${enabledCount} enabled eval${enabledCount === 1 ? '' : 's'}`);

  // 2. Resolve the scenario set to bind. A session targets exactly one set; on
  // multi-set hubs `--set`/`--eval` disambiguates. Without a flag we leave it
  // unbound and the run endpoint resolves the hub's sole enabled set.
  let scenarioSetId: string | undefined;
  if (setName) {
    const setsResult = await client.listScenarioSets(hubId);
    const match = setsResult.data.scenario_sets.find((s) => s.set_name === setName);
    if (!match) {
      const available = setsResult.data.scenario_sets.map((s) => s.set_name).join(', ') || '(none)';
      console.error(`Scenario set "${setName}" not found. Available: ${available}`);
      process.exit(1);
    }
    scenarioSetId = match.scenario_set_id;
  }

  // 2b. Resolve `--eval` names to the ephemeral scenario selection (gh #3748).
  // Names are resolved against the ENABLED evals — a disabled scenario can't run,
  // and saying so here beats a backend refusal after the session exists.
  let scenarioSelection: EvalScenarioSelection | undefined;
  if (evalNames.length > 0) {
    const selected = resolveSelectedEvals(evalNames, evalsResult.data.evals, setName, scenarioSetId);
    scenarioSetId = selected.scenarioSetId;
    // Range-check here, not only server-side: the backend refuses an out-of-range
    // run too, but only after the session row exists — same reason `--fixture` is
    // length-checked before a billable session is created. `--runs` implies
    // exactly one selected scenario, so `evals[0]` is the one it applies to.
    if (runNumbers) {
      const target = selected.evals[0];
      const configuredRuns = target.number_of_runs || 1;
      const outOfRange = runNumbers.filter((n) => n > configuredRuns);
      if (outOfRange.length > 0) {
        console.error(
          `Scenario "${target.eval_name}" is configured for ${configuredRuns} run${configuredRuns === 1 ? '' : 's'},`
          + ` so --runs ${outOfRange.join(',')} cannot be selected (valid: 1${configuredRuns > 1 ? `-${configuredRuns}` : ''}).`,
        );
        process.exit(1);
      }
    }
    scenarioSelection = selected.evals.map((e) => ({
      eval_id: e.eval_id,
      ...(runNumbers ? { run_numbers: runNumbers } : {}),
    }));
    const runCount = runNumbers
      ? runNumbers.length
      : selected.evals.reduce((sum, e) => sum + (e.number_of_runs || 1), 0);
    console.log(
      `Selected ${selected.evals.length} scenario${selected.evals.length === 1 ? '' : 's'}`
      + ` (${runCount} run${runCount === 1 ? '' : 's'})`
      + `${runNumbers ? `: run${runNumbers.length === 1 ? '' : 's'} ${runNumbers.join(', ')}` : ''}`,
    );
  }

  // 3. Create session
  const now = new Date();
  const sessionName = `CLI run ${now.toISOString().slice(0, 16).replace('T', ' ')}`;
  const createResult = await client.createEvalSession(hubId, sessionName, { scenarioSetId, pacing, scenarioSelection, callMode });
  if (callMode) console.log('Call mode: every turn runs as a call turn is written');
  const sessionId = createResult.data.session.eval_session_id;
  // Install as soon as the remote session exists: from here on, every phase can
  // be interrupted, and an interrupt that leaves the session running would let a
  // replacement run collide with it on the shared fixture (gh #3708).
  const signalController = installEvalSignalHandlers({
    hubId,
    sessionId,
    stop: () => client.stopEvalSession(hubId, sessionId),
  });
  try {
    await pollEvalSession({
      client, hubId, sessionId, sessionName, pacing, fixtureOverride, noWait, noQueue, timeoutSeconds,
      signal: signalController,
    });
  } finally {
    signalController.dispose();
  }
}

/**
 * Start the created session and follow it to a terminal status. Split out so the
 * caller can guarantee signal-handler teardown on every exit in one `finally`,
 * rather than at each of this function's returns.
 */
async function pollEvalSession(input: {
  client: ApiClient;
  hubId: string;
  sessionId: string;
  sessionName: string;
  pacing: PacingConfig | undefined;
  fixtureOverride: string | undefined;
  noWait: boolean;
  noQueue: boolean;
  timeoutSeconds: number;
  signal: EvalSignalController;
}): Promise<void> {
  const { client, hubId, sessionId, sessionName, pacing, fixtureOverride, noWait, timeoutSeconds } = input;
  // `--no-wait` means "don't hold my terminal", so it must not start holding it
  // on a busy fixture: a CI job that passes it precisely to free the runner would
  // otherwise block for the whole `--timeout`. It implies fail-fast queueing.
  const noQueue = input.noQueue || noWait;
  let queuedSeconds = 0;
  // ONE budget for the whole command: any time spent queueing on a busy fixture
  // comes out of the same `--timeout` the poll below spends, so `--timeout` stays
  // the honest upper bound on the command rather than a per-phase allowance.
  const runDeadlineMs = Date.now() + timeoutSeconds * 1000;
  const signalController = input.signal;
  const manualStopCommand = signalController.manualStopCommand;

  console.log(`Created session: ${sessionName} (${sessionId})`);
  if (pacing) {
    const desc = pacing.preset === 'custom' ? `custom (${pacing.custom_interval_ms}ms/run)` : pacing.preset;
    console.log(`Pacing: ${desc}`);
  }

  // 4. Run session — surface backend resolution errors (e.g. a multi-set hub
  // with no `--set`) instead of leaking a raw ApiError stack.
  //
  // A fixture held by another session is a WAIT, not a failure: one Rekor
  // preview admits one session at a time (gh #3745), and two CI branches sharing
  // a hub is normal. Queue inside the existing `--timeout` budget so a bounded
  // job stays bounded; `--no-queue` opts back into failing fast.
  try {
    const queueStartedMs = Date.now();
    const runResult = await runWithFixtureQueue({
      attempt: () => (fixtureOverride
        ? client.runEvalSession(hubId, sessionId, fixtureOverride)
        : client.runEvalSession(hubId, sessionId)),
      deadlineMs: runDeadlineMs,
      noQueue,
      now: () => Date.now(),
      sleep,
      // Ctrl-C during the wait exits through the already-installed stop path.
      interrupted: () => signalController.interrupted(),
      onWait: ({ message, waitMs }) => {
        console.log(`${message} Waiting ${Math.round(waitMs / 1000)}s…`);
      },
    });
    queuedSeconds = Math.round((Date.now() - queueStartedMs) / 1000);
    const seed = runResult.data.session.session_config?.seed;
    if (seed?.fixture) {
      console.log(`Fixture: ${seed.fixture}${seed.target_base ? ` (base: ${seed.target_base})` : ''}`);
    }
    for (const warning of runResult.data.seed_lease_warnings ?? []) {
      console.warn(`Warning: ${warning === 'SEED_LEASE_CAPACITY_95_PERCENT' ? '95%' : '80%'} of this preview base's seed-lease ledger is used. Replace the preview before it fills.`);
    }
  } catch (err) {
    // An interrupt mid-start surfaces here as a failed request; the handler is
    // already cancelling, so let it finish rather than reporting a start failure.
    if (await signalController.interrupted()) return;
    console.error(`\nFailed to start session: ${extractApiMessage(err)}`);
    // Printed HERE, not via `friendlyHint`: this catch exits the process, so a
    // top-level hint would never reach the user.
    // Both queueable refusals reach here only after the whole budget was spent
    // waiting, so the hint is the same for a busy fixture and for Rekor
    // backpressure: the wait was real, it just wasn't long enough.
    if (isQueueableLaunchError(err) && !noQueue) {
      console.error(`Waited ${timeoutSeconds}s for the fixture to become available. Raise --timeout, or use --no-queue to fail immediately.`);
    }
    console.error(`Session ID: ${sessionId}`);
    console.error(`Stop it if it started anyway: ${manualStopCommand}`);
    process.exit(1);
  }
  if (await signalController.interrupted()) return;
  console.log('Session started');

  // 5. If --no-wait, print session ID and exit
  if (noWait) {
    console.log(`\nSession ID: ${sessionId}`);
    console.log('Use `wayai eval-results --session ' + sessionId + '` to check results.');
    console.log(`Stop it early with: ${manualStopCommand}`);
    return;
  }

  // 6. Poll until terminal status — against the SAME budget the queue spent from.
  let consecutivePollErrors = 0;

  while (Date.now() < runDeadlineMs) {
    // Clamped, then re-checked: queueing makes entering this loop with a sliver
    // of budget routine, and an unclamped sleep would fetch results — and report
    // SUCCESS — after the deadline the command advertised.
    // Clamped, then re-checked: queueing makes entering this loop with a sliver
    // of budget routine, and an unclamped sleep would fetch results — and report
    // SUCCESS — after the deadline the command advertised.
    await sleep(Math.min(POLL_INTERVAL_MS, Math.max(0, runDeadlineMs - Date.now())));
    if (await signalController.interrupted()) return;
    if (Date.now() >= runDeadlineMs) break;

    // A single poll throwing must not tear down the live view — the session
    // keeps running server-side regardless. Ride out transient failures (token
    // rotation that survives the client refresh, KV propagation, network blip);
    // only give up once they persist past MAX_CONSECUTIVE_POLL_ERRORS (gh #2605).
    let details;
    try {
      details = await client.getEvalSessionDetails(hubId, sessionId);
      consecutivePollErrors = 0;
    } catch (err) {
      if (++consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        console.error(`\n\nLost connection to the eval session: ${extractApiMessage(err)}`);
        console.error(`The session may still be running. Stop it with: ${manualStopCommand}`);
        console.error(`Check results: wayai eval-results --session ${sessionId}`);
        process.exit(1);
      }
      continue;
    }

    const session = details.data.session;
    const results = details.data.results;

    // Calculate progress. Every terminal run counts toward it, whatever its
    // outcome — a run that errored is finished, not still going.
    const totalRuns = results.reduce((sum, r) => sum + r.total_runs, 0);
    const completedRuns = results.reduce((sum, r) => sum + terminalRunCount(r), 0);

    if (TERMINAL_STATUSES.has(session.session_status)) {
      // A signal that landed while this poll was in flight has already issued a
      // stop and owns the exit. Printing the summary here would interleave with
      // its output and be cut short mid-request by its process.exit.
      if (await signalController.interrupted()) return;
      console.log('');
      console.log(`Session ${session.session_status}: ${session.session_name} (${session.eval_session_id})\n`);
      printResultsTable(results, 'compact');

      // A red eval should explain itself here, not force a second
      // `eval-results --runs` (gh #2813). Pull per-run detail only when
      // something didn't pass, so the all-green path keeps its single round
      // trip. `failed_runs` covers errored runs too, and those need the detail
      // most of all — that's where the error message lives.
      if (results.some((r) => r.failed_runs > 0)) {
        try {
          const runsResult = await client.getEvalSessionRuns(hubId, sessionId);
          printFailingRuns(runsResult.data.runs);
        } catch (err) {
          // Best-effort: the summary already printed. Don't fail the command on
          // a detail-fetch hiccup — point the user at the standalone view.
          console.error(`\nCould not load failure details (${extractApiMessage(err)}).`);
          console.error(`See: wayai eval-results --session ${sessionId} --runs`);
        }
      }
      return;
    }

    process.stdout.write(`\rRunning... ${completedRuns}/${totalRuns} completed`);
  }

  // EXIT 1, not 0: this command's job is to report eval results, and it has none.
  // Exiting 0 here would let a CI job go green having scored nothing — which the
  // queue makes reachable, since waiting for a busy fixture spends the same
  // budget the polling needs (report the split so the cause is visible).
  console.error(`\n\nTimeout after ${timeoutSeconds}s${queuedSeconds > 0 ? ` (${queuedSeconds}s of it waiting for the fixture)` : ''}. Session is still running.`);
  console.error(`Stop it with: ${manualStopCommand}`);
  console.error(`Check results: wayai eval-results --session ${sessionId}`);
  process.exit(1);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Parse `--runs 2,6` into the scenario's OWN 1-based run numbers. These index
 * the scenario's per-run variables (run *i* reads `variables.NAME[i-1]`), so
 * they are never renumbered to 1..N — `--runs 2,6` scores exactly the fixtures
 * runs 2 and 6 use. Exits with a usage error on anything else.
 */
function parseRunNumbers(raw: string | undefined): number[] {
  if (!raw || raw.startsWith('--')) {
    console.error('--runs requires a comma-separated list of run numbers, e.g. --runs 2,6');
    process.exit(1);
  }
  const parts = raw.split(',').map((p) => p.trim()).filter((p) => p.length > 0);
  if (parts.length === 0) {
    console.error('--runs requires at least one run number, e.g. --runs 2,6');
    process.exit(1);
  }
  const runs: number[] = [];
  for (const part of parts) {
    const n = Number(part);
    // `String(n) === part` rejects "1e1", "0x2", "2.5" and other numeric-but-not-
    // integer forms that parseInt would silently truncate.
    if (!Number.isInteger(n) || String(n) !== part || n < 1 || n > EVAL_MAX_RUNS_PER_SCENARIO) {
      console.error(`--runs values must be whole numbers between 1 and ${EVAL_MAX_RUNS_PER_SCENARIO} (got "${part}")`);
      process.exit(1);
    }
    if (runs.includes(n)) {
      console.error(`--runs lists run ${n} more than once`);
      process.exit(1);
    }
    runs.push(n);
  }
  return runs.sort((a, b) => a - b);
}

type EnabledEval = { eval_id: string; eval_name: string; number_of_runs: number; scenario_set_id: string };

/**
 * Resolve `--eval` names to enabled scenarios, and to the single set they all
 * belong to. A session targets exactly one set, so scenarios spanning two sets
 * is a usage error, not something to silently split.
 *
 * `--set` narrows which candidates a name may match, which is what makes an eval
 * name reused across sets addressable at all; without it, an ambiguous name is
 * refused rather than resolved to whichever row came back first.
 */
function resolveSelectedEvals(
  names: string[],
  enabledEvals: EnabledEval[],
  setName: string | undefined,
  boundSetId: string | undefined,
): { evals: EnabledEval[]; scenarioSetId: string } {
  const candidates = boundSetId ? enabledEvals.filter((e) => e.scenario_set_id === boundSetId) : enabledEvals;
  const resolved: EnabledEval[] = [];

  for (const name of names) {
    const matches = candidates.filter((e) => e.eval_name === name);
    if (matches.length === 0) {
      console.error(
        `No enabled eval named "${name}"${setName ? ` in scenario set "${setName}"` : ''}.`
        + ' Run `wayai evals` to list (it may be disabled).',
      );
      process.exit(1);
    }
    if (matches.length > 1) {
      console.error(`Several enabled evals are named "${name}". Disambiguate with --set <name>.`);
      process.exit(1);
    }
    if (resolved.some((e) => e.eval_id === matches[0].eval_id)) {
      console.error(`--eval "${name}" was given more than once.`);
      process.exit(1);
    }
    resolved.push(matches[0]);
  }

  const setIds = [...new Set(resolved.map((e) => e.scenario_set_id))];
  if (setIds.length > 1) {
    console.error('The selected evals span multiple scenario sets. A session runs one set — select scenarios from a single set.');
    process.exit(1);
  }
  return { evals: resolved, scenarioSetId: setIds[0] };
}

/**
 * Parse `--pacing <value>` into a PacingConfig. Accepts a named preset
 * (conservative | balanced | fast) or an integer number of milliseconds
 * between runs (→ custom). Exits with a usage error on anything else.
 */
function parsePacing(raw: string | undefined): PacingConfig {
  const NAMED = EVAL_PACING_PRESET_NAMES.filter((p) => p !== 'custom'); // conservative | balanced | fast
  if (!raw) {
    console.error(`--pacing requires a value: ${NAMED.join(' | ')} | <milliseconds>`);
    process.exit(1);
  }
  const value = raw.trim();
  const asMs = Number(value);
  // A bare integer is a custom interval; String(asMs) === value rejects "1e3",
  // "0x10", "1500.5", and other numeric-but-not-integer forms.
  if (Number.isInteger(asMs) && String(asMs) === value) {
    if (asMs < PACING_INTERVAL_MIN_MS || asMs > PACING_INTERVAL_MAX_MS) {
      console.error(`--pacing interval must be between ${PACING_INTERVAL_MIN_MS} and ${PACING_INTERVAL_MAX_MS} milliseconds`);
      process.exit(1);
    }
    return { preset: 'custom', custom_interval_ms: asMs };
  }
  if (!(NAMED as readonly string[]).includes(value)) {
    console.error(`--pacing must be one of: ${NAMED.join(' | ')} | <milliseconds>`);
    process.exit(1);
  }
  return { preset: value as PacingConfig['preset'] };
}
